"use client"

import type React from "react"

import { useState } from "react"
import { Col, Container, Form, FormGroup, Input, Label, Row, Alert, Spinner } from "reactstrap"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import CommonLogo from "../Common/CommonLogo"
import { Btn, H4, P } from "../../../../AbstractElements"
import { Link } from "react-router-dom"

interface PasswordResetState {
  step: "email" | "otp" | "password"
  email: string
  otpCode: string
  newPassword: string
  confirmPassword: string
  resetToken: string
}

const ForgetPasswordContainer = () => {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const [state, setState] = useState<PasswordResetState>({
    step: "email",
    email: "",
    otpCode: "",
    newPassword: "",
    confirmPassword: "",
    resetToken: "",
  })

  const [otpInputs, setOtpInputs] = useState(["", "", "", "", "", ""])
  const baseURL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"

  // Handle email submission
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setSuccess("")

    try {
      const response = await fetch(`${baseURL}/api/accounts/password-reset/request/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: state.email }),
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess("OTP sent to your email address")
        setState((prev) => ({ ...prev, step: "otp" }))
      } else {
        setError(data.error || "Failed to send OTP")
      }
    } catch (error) {
      setError("Network error. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return

    const newOtpInputs = [...otpInputs]
    newOtpInputs[index] = value

    setOtpInputs(newOtpInputs)

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      nextInput?.focus()
    }

    // Update combined OTP code
    setState((prev) => ({ ...prev, otpCode: newOtpInputs.join("") }))
  }

  // Handle OTP verification
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const response = await fetch(`${baseURL}/api/accounts/password-reset/verify-otp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: state.email,
          otp_code: state.otpCode,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        setState((prev) => ({
          ...prev,
          step: "password",
          resetToken: data.reset_token,
        }))
        setSuccess("OTP verified successfully")
      } else {
        setError(data.error || "Invalid OTP code")
      }
    } catch (error) {
      setError("Network error. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // Handle password reset completion
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    // Validate passwords match
    if (state.newPassword !== state.confirmPassword) {
      setError("Passwords do not match")
      setLoading(false)
      return
    }

    try {
      const response = await fetch(`${baseURL}/api/accounts/password-reset/complete/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: state.email,
          reset_token: state.resetToken,
          new_password: state.newPassword,
          confirm_password: state.confirmPassword,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        await Swal.fire({
          title: "Success!",
          text: "Your password has been reset successfully. You can now login with your new password.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })
        navigate("/auth/login")
      } else {
        setError(data.error || data.errors?.new_password?.[0] || "Failed to reset password")
      }
    } catch (error) {
      setError("Network error. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // Handle resend OTP
  const handleResendOtp = async () => {
    setLoading(true)
    setError("")

    try {
      const response = await fetch(`${baseURL}/api/accounts/password-reset/resend-otp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: state.email }),
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess("New OTP sent to your email")
        setOtpInputs(["", "", "", "", "", ""])
        setState((prev) => ({ ...prev, otpCode: "" }))
      } else {
        setError(data.error || "Failed to resend OTP")
      }
    } catch (error) {
      setError("Network error. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page-wrapper">
      <Container fluid className="p-0">
        <Row>
          <Col xs="12">
            <div className="login-card login-dark">
              <div>
                <div>
                  <CommonLogo />
                </div>
                <div className="login-main">
                  {/* Error/Success Messages */}
                  {error && (
                    <Alert color="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert color="success" className="mb-3">
                      {success}
                    </Alert>
                  )}

                  {/* Step 1: Email Input */}
                  {state.step === "email" && (
                    <Form className="theme-form" onSubmit={handleEmailSubmit}>
                      <H4>Reset Your Password</H4>
                      <P className="text-muted mb-4">Enter your email address to receive an OTP</P>

                      <FormGroup>
                        <Label className="col-form-label">Email Address</Label>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={state.email}
                          onChange={(e) => setState((prev) => ({ ...prev, email: e.target.value }))}
                          required
                          disabled={loading}
                        />
                      </FormGroup>

                      <FormGroup className="mb-0">
                        <Btn color="primary" block className="w-100" disabled={loading || !state.email}>
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Sending OTP...
                            </>
                          ) : (
                            "Send OTP"
                          )}
                        </Btn>
                      </FormGroup>

                      <P className="mt-4 mb-0 text-center">
                        Remember your password?{" "}
                        <Link className="ms-2" to="/auth/login">
                          Sign In
                        </Link>
                      </P>
                    </Form>
                  )}

                  {/* Step 2: OTP Verification */}
                  {state.step === "otp" && (
                    <Form className="theme-form" onSubmit={handleOtpSubmit}>
                      <H4>Enter OTP</H4>
                      <P className="text-muted mb-4">We've sent a 6-digit code to {state.email}</P>

                      <FormGroup>
                        <Label className="col-form-label pt-0">Enter OTP Code</Label>
                        <Row>
                          {otpInputs.map((value, index) => (
                            <Col key={index}>
                              <Input
                                id={`otp-${index}`}
                                className="text-center opt-text"
                                type="text"
                                value={value}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                maxLength={1}
                                disabled={loading}
                                style={{ fontSize: "1.5rem", height: "50px" }}
                              />
                            </Col>
                          ))}
                        </Row>
                      </FormGroup>

                      <div className="mt-3 mb-4 text-center">
                        <span className="reset-password-link">
                          Didn't receive OTP?{" "}
                          <button
                            type="button"
                            className="btn-link text-danger border-0 bg-transparent p-0"
                            onClick={handleResendOtp}
                            disabled={loading}
                          >
                            Resend
                          </button>
                        </span>
                      </div>

                      <FormGroup className="mb-0">
                        <Btn color="primary" block className="w-100" disabled={loading || state.otpCode.length !== 6}>
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Verifying...
                            </>
                          ) : (
                            "Verify OTP"
                          )}
                        </Btn>
                      </FormGroup>

                      <P className="mt-4 mb-0 text-center">
                        <button
                          type="button"
                          className="btn-link text-primary border-0 bg-transparent p-0"
                          onClick={() => setState((prev) => ({ ...prev, step: "email" }))}
                        >
                          ← Back to Email
                        </button>
                      </P>
                    </Form>
                  )}

                  {/* Step 3: New Password */}
                  {state.step === "password" && (
                    <Form className="theme-form" onSubmit={handlePasswordSubmit}>
                      <H4>Create New Password</H4>
                      <P className="text-muted mb-4">Enter your new password</P>

                      <FormGroup>
                        <Label className="col-form-label">New Password</Label>
                        <div className="form-input position-relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter new password"
                            value={state.newPassword}
                            onChange={(e) => setState((prev) => ({ ...prev, newPassword: e.target.value }))}
                            required
                            disabled={loading}
                            minLength={8}
                          />
                          <div className="show-hide">
                            <span
                              onClick={() => setShowPassword(!showPassword)}
                              className={!showPassword ? "show" : ""}
                            />
                          </div>
                        </div>
                        <small className="text-muted">
                          Password must be at least 8 characters with uppercase, lowercase, and number
                        </small>
                      </FormGroup>

                      <FormGroup>
                        <Label className="col-form-label">Confirm Password</Label>
                        <div className="form-input position-relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm new password"
                            value={state.confirmPassword}
                            onChange={(e) => setState((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                            required
                            disabled={loading}
                          />
                          <div className="show-hide">
                            <span
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className={!showConfirmPassword ? "show" : ""}
                            />
                          </div>
                        </div>
                      </FormGroup>

                      <FormGroup className="mb-0">
                        <Btn
                          color="primary"
                          block
                          className="w-100"
                          disabled={loading || !state.newPassword || !state.confirmPassword}
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Resetting Password...
                            </>
                          ) : (
                            "Reset Password"
                          )}
                        </Btn>
                      </FormGroup>

                      <P className="mt-4 mb-0 text-center">
                        <Link className="ms-2" to="/auth/login">
                          Back to Sign In
                        </Link>
                      </P>
                    </Form>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ForgetPasswordContainer
